import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import ProfileModal, { TABS } from './ProfileModal';
import { miniState, loadMiniTeams } from './MiniTeamSection';
import { loadClubs } from './ClubTab';
import { loadTeams } from './TeamSection';
import { closeVis } from './Field';

/**
 * «Настройки» (/my-profile, formerly «Мой профиль»): a full page inside Flarum's user page
 * (hero + left user nav), like core «Настройки». Same tabs, fields and four buttons
 * as ProfileModal; the editor logic is shared with it (methods borrowed below).
 * «Выйти без сохранения» / «Сохранить и выйти» go back to the user's profile page.
 */
export default class ProfilePage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);
    if (!app.session.user) {
      m.route.set(app.route('index'));
      return;
    }
    this.show(app.session.user);
    app.setTitle(app.translator.trans('core.forum.settings.title'));
  }
  show(user) {
    super.show(user);
    if (this.state) return;
    this.tab = m.route.param('tab') || 'main';
    this.confirmExit = false;
    this.state = {
      result1: null,
      result2: null,
      checking: false,
      mini: miniState(user.attribute('ppk2MiniTeams'))
    };
    this.resetFields();
    loadClubs(this.state.club);
    loadTeams(this.state.team);
    loadMiniTeams(this.state.mini);
  }
  content() {
    if (!this.state) return null;
    return m("form", {
      className: "Ppk2ProfileModal Ppk2ProfilePage",
      onsubmit: e => this.onsubmit(e, false),
      onkeydown: e => this.onkeydownForm(e)
    }, this.content_(), m("div", {
      className: "Ppk2SwipeHint",
      "aria-hidden": "true"
    }));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.swipe = new TabSwipe(this);
    this.swipe.attach(this.element);
    this.lastTab = this.tab;
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    if (this.lastTab !== this.tab) {
      this.lastTab = this.tab;
      closeVis();
      this.moreOpen = false;
      this.swipe && this.swipe.revealTab();
    }
  }
  onremove(vnode) {
    this.swipe && this.swipe.detach();
    super.onremove(vnode);
  }

  /** Leave the page: back to the user's profile. */
  close() {
    this.confirmExit = false;
    m.route.set(app.route.user(this.user));
  }
  hide() {
    this.exitWithoutSaving();
  }
  onerror(error) {
    // Errors of the API are already shown as alerts by app.request.
    this.alertAttrs = null;
    m.redraw();
  }
}

// Borrow the editor logic from the modal (one implementation for both).
['resetFields', 'isDirty', 'selectTab', 'tabCarousel', 'exitWithoutSaving', 'revertAll', 'onkeydownForm', 'onsubmit'].forEach(k => {
  ProfilePage.prototype[k] = ProfileModal.prototype[k];
});
ProfilePage.prototype.content_ = ProfileModal.prototype.content;

/**
 * Phone only (≤ 767px): swipe the tab content left/right to go to the next/previous tab.
 * A gesture counts as a swipe only when it is clearly horizontal; vertical scrolling is untouched.
 * Never starts in text fields or in anything that scrolls horizontally itself (e.g. a wide
 * notifications table); on lists, the crop dialog and the confirm box a sideways move does nothing.
 */
const PHONE = '(max-width: 767px)';
const NO_SWIPE = 'select, .Ppk2Confirm, .Ppk2NoSwipe, .Modal';
class TabSwipe {
  constructor(page) {
    this.page = page;
    this.s = null;
    this.busy = false;
    this.start = this.start.bind(this);
    this.move = this.move.bind(this);
    this.end = this.end.bind(this);
  }
  attach(el) {
    this.el = el;
    // a sideways swipe must not become the browser's «back/forward» overscroll gesture
    document.documentElement.classList.add('Ppk2NoOverscroll');
    el.addEventListener('touchstart', this.start, {
      passive: true
    });
    el.addEventListener('touchmove', this.move, {
      passive: false
    });
    el.addEventListener('touchend', this.end);
    el.addEventListener('touchcancel', this.end);
  }
  detach() {
    const el = this.el;
    document.documentElement.classList.remove('Ppk2NoOverscroll');
    if (!el) return;
    el.removeEventListener('touchstart', this.start);
    el.removeEventListener('touchmove', this.move);
    el.removeEventListener('touchend', this.end);
    el.removeEventListener('touchcancel', this.end);
  }
  index() {
    return TABS.findIndex(([id]) => id === this.page.tab);
  }

  /** Tab index a swipe by dx leads to; wraps around (Награды → Главная and back). */
  neighbour(dx) {
    const n = TABS.length;
    return (this.index() + (dx < 0 ? 1 : n - 1)) % n;
  }
  scrolls(target, body) {
    for (let n = target; n && n !== body; n = n.parentElement) {
      const ox = getComputedStyle(n).overflowX;
      if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 1) return true;
    }
    return false;
  }
  start(e) {
    if (this.busy || !window.matchMedia(PHONE).matches || e.touches.length !== 1) return;
    const body = e.target.closest('.Ppk2ProfilePage .Ppk2ProfileModal-body');
    if (!body || this.page.confirmExit) return;
    // text fields keep their own gestures (caret, selection); horizontally scrollable parts scroll
    if (e.target.closest('input:not([type=checkbox]):not([type=radio]), textarea, [contenteditable], .Ppk2Crop') || this.scrolls(e.target, body)) return;
    const p = e.touches[0];
    // on lists, the crop dialog etc. a sideways move is swallowed (no tab change, no browser «back»)
    const eat = !!e.target.closest(NO_SWIPE);
    this.s = {
      x: p.clientX,
      y: p.clientY,
      t: Date.now(),
      dx: 0,
      lock: null,
      body,
      w: body.offsetWidth,
      eat
    };
  }
  move(e) {
    const s = this.s;
    if (!s) return;
    const p = e.touches[0];
    let dx = p.clientX - s.x;
    const dy = p.clientY - s.y;
    if (!s.lock) {
      if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return;
      s.lock = Math.abs(dx) > Math.abs(dy) * 1.4 ? 'x' : 'y';
      if (s.lock === 'y') {
        this.s = null; // normal vertical scroll
        return;
      }
    }
    e.preventDefault();
    if (s.eat) return;
    const to = this.neighbour(dx);
    s.dx = dx;
    s.body.style.transition = 'none';
    s.body.style.transform = `translateX(${dx}px)`;
    s.body.style.opacity = String(1 - Math.min(Math.abs(dx) / s.w, 1) * 0.5);
    this.hint(to, dx, s.w);
  }
  hint(to, dx, w) {
    const h = this.el.querySelector('.Ppk2SwipeHint');
    if (!h) return;
    if (to < 0 || to >= TABS.length || !dx) {
      h.className = 'Ppk2SwipeHint';
      return;
    }
    const label = app.translator.trans('ppk2-profile.forum.' + TABS[to][1] + '_short');
    h.textContent = dx < 0 ? label + ' ›' : '‹ ' + label;
    h.className = 'Ppk2SwipeHint visible ' + (dx < 0 ? 'right' : 'left');
    h.style.opacity = String(Math.min(Math.abs(dx) / (w * 0.25), 1));
  }
  end() {
    const s = this.s;
    this.s = null;
    if (!s || s.lock !== 'x' || s.eat) return;
    const to = this.neighbour(s.dx);
    const fast = Math.abs(s.dx) > 40 && Math.abs(s.dx) / Math.max(Date.now() - s.t, 1) > 0.5;
    this.hint(-1, 0, s.w);
    if (to >= 0 && to < TABS.length && (Math.abs(s.dx) > s.w * 0.25 || fast)) this.go(to, s.dx < 0 ? -1 : 1, s.body);else this.settle(s.body);
  }
  settle(body) {
    body.style.transition = 'transform .2s ease, opacity .2s ease';
    body.style.transform = '';
    body.style.opacity = '';
  }

  /** Slide the old tab out, switch, slide the new one in from the other side. */
  go(to, dir, body) {
    this.busy = true;
    const w = body.offsetWidth;
    body.style.transition = 'transform .16s ease-in, opacity .16s ease-in';
    body.style.transform = `translateX(${dir * w}px)`;
    body.style.opacity = '0';
    setTimeout(() => {
      this.page.tab = TABS[to][0];
      m.redraw.sync();
      const nb = this.el.querySelector('.Ppk2ProfileModal-body');
      nb.style.transition = 'none';
      nb.style.transform = `translateX(${-dir * w * 0.6}px)`;
      nb.style.opacity = '0';
      void nb.offsetWidth;
      nb.style.transition = 'transform .2s ease-out, opacity .2s ease-out';
      nb.style.transform = '';
      nb.style.opacity = '';
      this.busy = false;
    }, 170);
  }

  /** Keep the active tab visible in the strip; on a phone bring the carousel back to the top. */
  revealTab() {
    const strip = this.el.querySelector('.Ppk2ProfilePage .Ppk2Tabs');
    const tab = strip && strip.querySelector('.Ppk2Tabs-tab.active');
    if (tab && strip.offsetParent) strip.scrollTo({
      left: tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2,
      behavior: 'smooth'
    });
    if (window.matchMedia(PHONE).matches) {
      const carousel = this.el.querySelector('.Ppk2ProfilePage .Ppk2TabCarousel');
      if (!carousel) return;
      const top = carousel.getBoundingClientRect().top + window.scrollY - (document.querySelector('.App-header')?.offsetHeight || 0);
      if (window.scrollY > top) window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  }
}
flarum.reg.add('ppk2-profile', 'forum/components/ProfilePage', ProfilePage);